/**
 * TimePicker - Time selection component
 */

class TimePicker {
    constructor(options = {}) {
      this.options = {
        format: '12',          // '12' or '24'
        language: 'en',        // 'en' or 'ne'
        minTime: null,         // Minimum time {hour, minute}
        maxTime: null,         // Maximum time {hour, minute}
        minuteStep: 1,         // Step for minute selection (changed from 5 to 1)
        onChange: null,        // Callback when time changes
        ...options
      };
      
      this.currentTime = this._getCurrentTime();
      this.selectedTime = null;
      this.container = null;
      this.initialized = false;
    }
  
    /**
     * Initialize the time picker
     * @param {HTMLElement} container - Container element
     */
    initialize(container) {
      if (!container) {
        throw new Error('Container element is required');
      }
      
      this.container = container;
      this.initialized = true;
      
      this._render();
      this._attachEventListeners();
    }
  
    /**
     * Get current time
     * @private
     */
    _getCurrentTime() {
      const now = new Date();
      return {
        hour: now.getHours(),
        minute: now.getMinutes(),
        second: now.getSeconds()
      };
    }
  
    /**
     * Render the time picker
     * @private
     */
    _render() {
      if (!this.initialized) return;
      
      this.container.innerHTML = '';
      
      const timePickerWrapper = document.createElement('div');
      timePickerWrapper.className = 'ndtp-time-picker';
      
      // Time display
      const timeDisplay = this._createTimeDisplay();
      timePickerWrapper.appendChild(timeDisplay);
      
      // Time controls
      const timeControls = this._createTimeControls();
      timePickerWrapper.appendChild(timeControls);
      
      this.container.appendChild(timePickerWrapper);
    }
  
    /**
     * Create time display
     * @private
     */
    _createTimeDisplay() {
      const display = document.createElement('div');
      display.className = 'ndtp-time-display';
      
      const timeString = this._formatTime(this.selectedTime || this.currentTime);
      display.textContent = timeString;
      
      return display;
    }
  
    /**
     * Create time controls
     * @private
     */
    _createTimeControls() {
      const controls = document.createElement('div');
      controls.className = 'ndtp-time-controls';
      
      // Hour control
      const hourControl = this._createHourControl();
      controls.appendChild(hourControl);
      
      // Separator
      const separator = document.createElement('span');
      separator.className = 'ndtp-time-separator';
      separator.textContent = ':';
      controls.appendChild(separator);
      
      // Minute control
      const minuteControl = this._createMinuteControl();
      controls.appendChild(minuteControl);
      
      // AM/PM control (for 12-hour format)
      if (this.options.format === '12') {
        const ampmControl = this._createAmPmControl();
        controls.appendChild(ampmControl);
      }
      
      return controls;
    }
  
    /**
     * Create hour control
     * @private
     */
    _createHourControl() {
      const control = document.createElement('div');
      control.className = 'ndtp-time-control ndtp-hour-control';
      
      const upButton = document.createElement('button');
      upButton.className = 'ndtp-time-btn ndtp-time-up';
      upButton.innerHTML = '▲';
      upButton.type = 'button';
      upButton.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._incrementHour();
      };
      
      const hourInput = document.createElement('input');
      hourInput.className = 'ndtp-time-input ndtp-hour-input';
      hourInput.type = 'text';
      hourInput.value = this._formatHour(this.selectedTime?.hour ?? this.currentTime.hour);
      hourInput.maxLength = 2;
      
      // Make input editable
      hourInput.readOnly = false;
      hourInput.onclick = (e) => {
        e.stopPropagation();
        hourInput.select();
      };
      
      hourInput.onchange = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const value = parseInt(e.target.value, 10);
        if (!isNaN(value)) {
          this._setHour(value);
        } else {
          hourInput.value = this._formatHour(this.selectedTime?.hour ?? this.currentTime.hour);
        }
      };
      
      const downButton = document.createElement('button');
      downButton.className = 'ndtp-time-btn ndtp-time-down';
      downButton.innerHTML = '▼';
      downButton.type = 'button';
      downButton.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._decrementHour();
      };
      
      control.appendChild(upButton);
      control.appendChild(hourInput);
      control.appendChild(downButton);
      
      return control;
    }
  
    /**
     * Create minute control
     * @private
     */
    _createMinuteControl() {
      const control = document.createElement('div');
      control.className = 'ndtp-time-control ndtp-minute-control';
      
      const upButton = document.createElement('button');
      upButton.className = 'ndtp-time-btn ndtp-time-up';
      upButton.innerHTML = '▲';
      upButton.type = 'button';
      upButton.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._incrementMinute();
      };
      
      const minuteInput = document.createElement('input');
      minuteInput.className = 'ndtp-time-input ndtp-minute-input';
      minuteInput.type = 'text';
      minuteInput.value = this._formatMinute(this.selectedTime?.minute ?? this.currentTime.minute);
      minuteInput.maxLength = 2;
      
      // Make input editable
      minuteInput.readOnly = false;
      minuteInput.onclick = (e) => {
        e.stopPropagation();
        minuteInput.select();
      };
      
      minuteInput.onchange = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const value = parseInt(e.target.value, 10);
        if (!isNaN(value)) {
          this._setMinute(value);
        } else {
          minuteInput.value = this._formatMinute(this.selectedTime?.minute ?? this.currentTime.minute);
        }
      };
      
      const downButton = document.createElement('button');
      downButton.className = 'ndtp-time-btn ndtp-time-down';
      downButton.innerHTML = '▼';
      downButton.type = 'button';
      downButton.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._decrementMinute();
      };
      
      control.appendChild(upButton);
      control.appendChild(minuteInput);
      control.appendChild(downButton);
      
      return control;
    }
  
    /**
     * Create AM/PM control
     * @private
     */
    _createAmPmControl() {
      const control = document.createElement('div');
      control.className = 'ndtp-time-control ndtp-ampm-control';
      
      const isPM = (this.selectedTime?.hour ?? this.currentTime.hour) >= 12;
      
      const toggleButton = document.createElement('button');
      toggleButton.className = 'ndtp-time-btn ndtp-ampm-btn';
      toggleButton.textContent = isPM ? 'PM' : 'AM';
      toggleButton.type = 'button';
      toggleButton.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._toggleAmPm();
      };
      
      control.appendChild(toggleButton);
      
      return control;
    }
  
    /**
     * Increment hour
     * @private
     */
    _incrementHour() {
      let time = this._ensureSelectedTime();
      time.hour = (time.hour + 1) % 24;
      
      if (!this._isValidTime(time)) {
        return;
      }
      
      this.selectedTime = time;
      this._updateDisplay();
      this._triggerChange();
    }
  
    /**
     * Decrement hour
     * @private
     */
    _decrementHour() {
      let time = this._ensureSelectedTime();
      time.hour = (time.hour - 1 + 24) % 24;
      
      if (!this._isValidTime(time)) {
        return;
      }
      
      this.selectedTime = time;
      this._updateDisplay();
      this._triggerChange();
    }
  
    /**
     * Set hour directly
     * @private
     */
    _setHour(hour) {
      let time = this._ensureSelectedTime();
      
      // Handle 12-hour format input
      if (this.options.format === '12') {
        const isPM = (time.hour ?? this.currentTime.hour) >= 12;
        if (hour > 12) {
          hour = hour % 12;
        }
        if (hour === 0) {
          hour = 12;
        }
        if (isPM && hour !== 12) {
          hour += 12;
        } else if (!isPM && hour === 12) {
          hour = 0;
        }
      } else {
        // 24-hour format
        hour = hour % 24;
      }
      
      time.hour = hour;
      
      if (!this._isValidTime(time)) {
        return;
      }
      
      this.selectedTime = time;
      this._updateDisplay();
      this._triggerChange();
    }
  
    /**
     * Increment minute
     * @private
     */
    _incrementMinute() {
      let time = this._ensureSelectedTime();
      time.minute = (time.minute + this.options.minuteStep) % 60;
      
      if (time.minute < (this.selectedTime?.minute ?? this.currentTime.minute)) {
        time.hour = (time.hour + 1) % 24;
      }
      
      if (!this._isValidTime(time)) {
        return;
      }
      
      this.selectedTime = time;
      this._updateDisplay();
      this._triggerChange();
    }
  
    /**
     * Decrement minute
     * @private
     */
    _decrementMinute() {
      let time = this._ensureSelectedTime();
      time.minute = time.minute - this.options.minuteStep;
      
      if (time.minute < 0) {
        time.minute = 60 + time.minute;
        time.hour = (time.hour - 1 + 24) % 24;
      }
      
      if (!this._isValidTime(time)) {
        return;
      }
      
      this.selectedTime = time;
      this._updateDisplay();
      this._triggerChange();
    }
  
    /**
     * Set minute directly
     * @private
     */
    _setMinute(minute) {
      let time = this._ensureSelectedTime();
      
      minute = minute % 60;
      time.minute = minute;
      
      if (!this._isValidTime(time)) {
        return;
      }
      
      this.selectedTime = time;
      this._updateDisplay();
      this._triggerChange();
    }
  
    /**
     * Toggle AM/PM
     * @private
     */
    _toggleAmPm() {
      let time = this._ensureSelectedTime();
      time.hour = (time.hour + 12) % 24;
      
      if (!this._isValidTime(time)) {
        return;
      }
      
      this.selectedTime = time;
      this._updateDisplay();
      this._triggerChange();
    }
  
    /**
     * Ensure selected time is set
     * @private
     */
    _ensureSelectedTime() {
      if (!this.selectedTime) {
        this.selectedTime = { ...this.currentTime };
      }
      return { ...this.selectedTime };
    }
  
    /**
     * Check if time is valid
     * @private
     */
    _isValidTime(time) {
      if (this.options.minTime) {
        if (time.hour < this.options.minTime.hour ||
            (time.hour === this.options.minTime.hour && time.minute < this.options.minTime.minute)) {
          return false;
        }
      }
      
      if (this.options.maxTime) {
        if (time.hour > this.options.maxTime.hour ||
            (time.hour === this.options.maxTime.hour && time.minute > this.options.maxTime.minute)) {
          return false;
        }
      }
      
      return true;
    }
  
    /**
     * Format time for display
     * @private
     */
    _formatTime(time) {
      if (this.options.format === '12') {
        let hour = time.hour % 12;
        if (hour === 0) hour = 12;
        const ampm = time.hour >= 12 ? 'PM' : 'AM';
        return `${this._formatHour(hour)}:${this._formatMinute(time.minute)} ${ampm}`;
      } else {
        return `${this._formatHour(time.hour)}:${this._formatMinute(time.minute)}`;
      }
    }
  
    /**
     * Format hour
     * @private
     */
    _formatHour(hour) {
      if (this.options.format === '12') {
        hour = hour % 12;
        if (hour === 0) hour = 12;
      }
      const formattedHour = hour.toString().padStart(2, '0');
      return this.options.language === 'ne' ? this._toNepaliDigits(formattedHour) : formattedHour;
    }
  
    /**
     * Format minute
     * @private
     */
    _formatMinute(minute) {
      const formattedMinute = minute.toString().padStart(2, '0');
      return this.options.language === 'ne' ? this._toNepaliDigits(formattedMinute) : formattedMinute;
    }
  
    /**
     * Convert to Nepali digits
     * @private
     */
    _toNepaliDigits(text) {
      const nepaliDigits = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
      return String(text).replace(/\d/g, digit => nepaliDigits[digit]);
    }
  
    /**
     * Update display
     * @private
     */
    _updateDisplay() {
      const timeDisplay = this.container.querySelector('.ndtp-time-display');
      if (timeDisplay) {
        timeDisplay.textContent = this._formatTime(this.selectedTime || this.currentTime);
      }
      
      const hourInput = this.container.querySelector('.ndtp-hour-input');
      if (hourInput) {
        hourInput.value = this._formatHour(this.selectedTime?.hour ?? this.currentTime.hour);
      }
      
      const minuteInput = this.container.querySelector('.ndtp-minute-input');
      if (minuteInput) {
        minuteInput.value = this._formatMinute(this.selectedTime?.minute ?? this.currentTime.minute);
      }
      
      const ampmBtn = this.container.querySelector('.ndtp-ampm-btn');
      if (ampmBtn) {
        const isPM = (this.selectedTime?.hour ?? this.currentTime.hour) >= 12;
        ampmBtn.textContent = isPM ? 'PM' : 'AM';
      }
    }
  
    /**
     * Attach event listeners
     * @private
     */
    _attachEventListeners() {
      // Prevent unwanted propagation
      this.container.addEventListener('click', (e) => {
        e.stopPropagation();
      });
      
      // Add keyboard support
      this.container.addEventListener('keydown', (e) => {
        switch (e.key) {
          case 'ArrowUp':
            e.preventDefault();
            if (e.target.closest('.ndtp-hour-control')) {
              this._incrementHour();
            } else if (e.target.closest('.ndtp-minute-control')) {
              this._incrementMinute();
            }
            break;
          case 'ArrowDown':
            e.preventDefault();
            if (e.target.closest('.ndtp-hour-control')) {
              this._decrementHour();
            } else if (e.target.closest('.ndtp-minute-control')) {
              this._decrementMinute();
            }
            break;
          case 'Tab':
            // Allow default tab behavior
            break;
          case 'Enter':
            e.preventDefault();
            if (e.target.classList.contains('ndtp-time-input')) {
              e.target.blur();
            }
            break;
        }
      });
    }
  
    /**
     * Trigger change event
     * @private
     */
    _triggerChange() {
      if (this.options.onChange && this.selectedTime) {
        this.options.onChange(this.selectedTime);
      }
    }
  
    /**
     * Set time
     * @param {Object} time - Time object {hour, minute}
     */
    setTime(time) {
      if (!this._isValidTime(time)) {
        throw new Error('Invalid time');
      }
      
      this.selectedTime = { ...time };
      this._updateDisplay();
    }
  
    /**
     * Get time
     * @returns {Object} Time object {hour, minute}
     */
    getTime() {
      // Return selected time if available, otherwise return current time as default
      if (!this.selectedTime) {
        // Initialize with current time if nothing is selected
        this.selectedTime = { ...this.currentTime };
      }
      return { ...this.selectedTime };
    }
  
    /**
     * Clear selection
     */
    clear() {
      this.selectedTime = null;
      this._updateDisplay();
    }
  
    /**
     * Destroy the time picker
     */
    destroy() {
      if (this.container) {
        this.container.innerHTML = '';
      }
      this.initialized = false;
    }
  }
  
  export default TimePicker;